/**
 * The pure per-token turn-state + vitals derivation the board draws (`projects/turn-state-on-the-map.md`,
 * `decisions/0178` points 1–3). One function, {@link tokenChromeFor}, turns the encounter into a
 * {@link TokenChrome} per combatant; `pixiCombatBoard.ts` draws exactly that and reads nothing
 * turn- or vitals-related off the raw `Combatant`.
 *
 * It replaced the TURN rail's chip derivation (`turnChipSections`, deleted with the rail,
 * `decisions/0190`) and keeps its rules:
 *   - **`state` is set only for actors.** `isActor` is mech | npc | pilot, and `perUnitState` emits
 *     no entry for a drone, object or deployable (`packages/domain/src/encounter/activation.ts`
 *     says why that absence is load-bearing). A non-actor's `state` is `null`, so it never wears a
 *     turn mark; it still carries vitals.
 *   - **`activations`** is `{ used, max }` only for a unit whose `maxActivations` exceeds 1 (an
 *     ELITE/ULTRA NPC), off the same `state.activation.activationsUsed` tally the rail and the CLI
 *     read.
 *   - **The vitals are read off {@link projectForDisplay}'s output, never the raw combatant.** The
 *     wire carries every visible combatant's full data and only `projectForDisplay` redacts
 *     (`decisions/0082`), so this is the fifth `DisplayCombatant` chokepoint. A masked hostile, or
 *     an HP-hidden one, gets `hp: null`: the board draws no bar at all, never `0/max`.
 */
import {
  HIDDEN_STATUS,
  OVERSHIELD_RESOURCE,
  isActor,
  maxActivations,
  projectForDisplay,
  type ActorRole,
  type CombatantId,
  type EncounterState,
  type UnitActivationState,
} from "@lancer-vtt/domain";
import { isHpVisible, isMaskedCombatant } from "./disclosure";

/** Who is looking — the same pair `projectForDisplay` takes. */
export type TokenChromeViewer = {
  /** The viewer's slot id, or `null` for a viewer with no owning slot. */
  viewerSlot: string | null;
  role: ActorRole;
};

/** A track the token draws as a bar or a pip row. */
export type TokenTrack = { cur: number; max: number };

/** Everything turn-related and vital one token draws. */
export type TokenChrome = {
  /** The engine's activation state, or `null` for a non-actor (drone, object, deployable). */
  state: UnitActivationState | null;
  /** The multi-activation badge, or `null` when the unit takes one turn a round. */
  activations: { used: number; max: number } | null;
  /** Current HP, or `null` when this viewer may not see it (masked or HP-hidden): no bar is drawn. */
  hp: number | null;
  /** Max HP, or `null` for a masked unit, whose stat block is redacted. */
  maxHp: number | null;
  /** Heat, `null` for a kind with no heat track or a masked unit. */
  heat: TokenTrack | null;
  /** Structure, `null` for a kind with no structure track, a single-structure unit, or a masked unit. */
  structure: TokenTrack | null;
  /** Overshield points (0 when none). */
  overshield: number;
  /** One-letter glyphs from {@link STATUS_GLYPH}, in the unit's status order; unlisted statuses are skipped. */
  statuses: string[];
};

/**
 * One-letter glyph per status that changes what a unit can do (`turn-state-on-the-map` 1.0, the
 * developer accepted the table 2026-09-25). Hidden and fog concealment are NOT here: they have
 * their own token marks (the holo ghost, the dashed footprint square).
 */
export const STATUS_GLYPH: Readonly<Record<string, string>> = {
  Impaired: "I",
  Slowed: "S",
  Jammed: "J",
  "Lock On": "L",
  Shredded: "X",
  Immobilized: "M",
  Stunned: "!",
  Prone: "P",
  Exposed: "E",
  "Down and Out": "D",
  "Shut Down": "Z",
  Grappled: "G",
  Invisible: "V",
  Braced: "B",
  Bolster: "+",
  Flying: "F",
  Intangible: "T",
  Unpiloted: "U",
  Cascading: "C",
};

/**
 * The chrome for every combatant in `state.combatants`, keyed by id. `states` is `perUnitState(state)`,
 * computed once by the caller; an actor missing from it reads `"on_deck"`, the pre-turn default the
 * rail used.
 */
export function tokenChromeFor(
  state: EncounterState,
  states: Record<CombatantId, UnitActivationState>,
  viewer: TokenChromeViewer,
): Record<CombatantId, TokenChrome> {
  const out: Record<CombatantId, TokenChrome> = {};
  for (const c of Object.values(state.combatants)) {
    const actor = isActor(c);
    const max = actor ? maxActivations(state, c) : 1;
    const shown = projectForDisplay({ combatant: c, viewerSlot: viewer.viewerSlot, role: viewer.role, state });
    const statuses = shown.statuses.flatMap((s) => (s.name !== HIDDEN_STATUS && STATUS_GLYPH[s.name] ? [STATUS_GLYPH[s.name]!] : []));
    const turn = {
      state: actor ? (states[c.id] ?? "on_deck") : null,
      activations: max > 1 ? { used: state.activation.activationsUsed[c.id] ?? 0, max } : null,
    };
    if (isMaskedCombatant(shown)) {
      out[c.id] = { ...turn, hp: null, maxHp: null, heat: null, structure: null, overshield: 0, statuses };
      continue;
    }
    const cur = shown.current;
    const { maxHeat, maxStructure } = shown.base;
    out[c.id] = {
      ...turn,
      hp: isHpVisible(cur) ? cur.hp : null,
      maxHp: shown.base.maxHp,
      heat: maxHeat != null ? { cur: cur.heat ?? 0, max: maxHeat } : null,
      structure: maxStructure != null && maxStructure > 1 ? { cur: cur.structure ?? maxStructure, max: maxStructure } : null,
      overshield: cur.resources?.[OVERSHIELD_RESOURCE] ?? 0,
      statuses,
    };
  }
  return out;
}

/**
 * A stable string for one token's chrome — `tokenStyleKey` appends it so a state or vitals flip
 * rebuilds that token and nothing else does.
 */
export function tokenChromeKey(chrome: TokenChrome | undefined): string {
  if (!chrome) return "-";
  const t = (x: TokenTrack | null) => (x ? `${x.cur}/${x.max}` : "-");
  const a = chrome.activations;
  return [chrome.state ?? "-", a ? `${a.used}/${a.max}` : "-", chrome.hp ?? "-", chrome.maxHp ?? "-", t(chrome.heat), t(chrome.structure), chrome.overshield, chrome.statuses.join("")].join(";");
}
